import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { brandingToCssVars, resolveBranding, resolveBrandingWithOverrides } from '@feed-widget/branding'
import { loadFonts } from '@feed-widget/utils/fonts'
import '@feed-widget/surfaces/PrepacksSurface.css'
import { useOrganization } from '@/contexts/OrganizationContext'
import { organizationApi, prepacksApi } from '@/services/api'
import { PrepackPreviewContext, usePrepackPreview } from './prepackPreviewContext'
import { getFallbackTemplate, normalizeTemplates } from '../../../../../shared/pkg/prepacks/templateVariants.js'


function PreviewProvider({ branding, fallbackTemplate, surface = null, oddsFormat = 'american', unavailable = false, children }) {
  const value = useMemo(() => ({ branding, cssVars: brandingToCssVars(branding), fallbackTemplate, surface, oddsFormat, unavailable }), [branding, fallbackTemplate, surface, oddsFormat, unavailable])
  useEffect(() => { loadFonts(branding.fontUrls) }, [branding.fontUrls])
  return <PrepackPreviewContext.Provider value={value}>{children}</PrepackPreviewContext.Provider>
}

export function PrepackPreviewProvider({ children }) {
  const { currentOrg } = useOrganization()
  const orgId = currentOrg?.id
  const brandingQuery = useQuery({
    queryKey: ['branding', orgId],
    queryFn: () => organizationApi.getBranding(orgId),
    enabled: Boolean(orgId),
  })
  const configQuery = useQuery({
    queryKey: ['prepacks', 'configuration', currentOrg?.slug],
    queryFn: () => prepacksApi.getConfiguration(currentOrg.slug),
    enabled: Boolean(currentOrg?.slug),
  })
  const fallbackTemplate = useMemo(() => getFallbackTemplate(normalizeTemplates(configQuery.data?.card_templates)), [configQuery.data])
  const branding = useMemo(() => resolveBranding(brandingQuery.data || {}), [brandingQuery.data])
  return (
    <PreviewProvider branding={branding} fallbackTemplate={fallbackTemplate} oddsFormat={configQuery.data?.odds_format === 'decimal' ? 'decimal' : 'american'} unavailable={brandingQuery.isPending || brandingQuery.isError || configQuery.isPending || configQuery.isError}>
      {brandingQuery.isError ? <div role="alert" className="border-b border-[var(--prepacks-border)] p-3 text-sm">Branding could not be loaded. Card previews are unavailable. <button type="button" className="underline" onClick={() => brandingQuery.refetch()}>Retry branding</button></div> : null}
      {configQuery.isError ? <div role="alert" className="border-b border-[var(--prepacks-border)] p-3 text-sm">Card configuration could not be loaded. Card previews are unavailable. <button type="button" className="underline" onClick={() => configQuery.refetch()}>Retry configuration</button></div> : null}
      {children}
    </PreviewProvider>
  )
}

export function PrepackSurfacePreviewProvider({ surface, children }) {
  const { branding: baseBranding, fallbackTemplate, oddsFormat, unavailable } = usePrepackPreview()
  const branding = useMemo(() => resolveBrandingWithOverrides(baseBranding, surface?.theme_overrides), [baseBranding, surface?.theme_overrides])
  return <PreviewProvider branding={branding} fallbackTemplate={fallbackTemplate} surface={surface} oddsFormat={oddsFormat} unavailable={unavailable}>{children}</PreviewProvider>
}
